import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Spinner } from '~/ui';
import { instance } from '~/utils/api/api';
import { useNavigate } from 'react-router-dom';
import styles from './AssetStorageFolders.module.scss';
import { useToastError } from '~/utils/useToastError';
import { AssetFolderCard } from '../AssetFolderCard/AssetFolderCard';
import { IFolders } from '~/utils/types/storage';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useTranslation } from 'react-i18next';

interface IAssetFoldersProps {
  folderId: string | undefined;
  assetId: string | undefined;
  refetchAsset: () => void;
}

export const AssetStorageFolders = ({
  assetId,
  folderId,
  refetchAsset,
}: IAssetFoldersProps) => {
  const [editedNameId, setEditedNameId] = useState<null | number>(null);
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const navigate = useNavigate();
  const client = useQueryClient();

  const { data: assetFolders, isLoading } = useQuery<IFolders[]>({
    queryFn: async () => {
      const { data } = await instance.get('admin/folders', {
        params: folderId ? { folder_id: folderId } : { asset_id: assetId },
      });
      return data;
    },
    queryKey: ['asset-folders', assetId, folderId],
    onError: error => {
      toastError(error);
    },
  });

  const { mutateAsync: updateFolder, isLoading: isUpdating } = useMutation(
    async (name: string) => {
      const response = await instance.put(`admin/folders/${editedNameId}`, {
        name,
      });
      return response;
    },
    {
      onSuccess: () => {
        toastSuccess(t('folder_successfull_updated'));
        client.invalidateQueries(['asset-folders']);
        setEditedNameId(null);
      },
      onError: () => {
        setEditedNameId(null);
      },
    }
  );

  const { mutateAsync: deleteFolder } = useMutation(
    async (id: number) => {
      const response = await instance.delete(`admin/folders/${id}`);
      return response;
    },
    {
      onSuccess: () => {
        toastSuccess(t('deleted_successfully'));
        client.invalidateQueries(['asset-folders']);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleEditName = (value: string) => {
    updateFolder(value);
  };

  const handleSetActiveFolder = (value: number) => {
    navigate(`/documents/${assetId}/${value}`);
  };

  return (
    <div className={styles.mainContainer}>
      {assetFolders && !isLoading ? (
        <div className={styles.contentWrapper}>
          <div className={styles.folderTitleWrapper}>
            <p className={styles.folderTitle}>{t('folders')}</p>
          </div>
          {assetFolders?.length ? (
            <ul className={styles.folderList}>
              {assetFolders?.map(item => (
                <li
                  key={item.id}
                  value={String(item.id)}
                  className={styles.trigger}
                >
                  <AssetFolderCard
                    setActiveFolder={handleSetActiveFolder}
                    onClick={handleEditName}
                    setEditedNameId={setEditedNameId}
                    editedNameId={editedNameId}
                    refetchAsset={refetchAsset}
                    data={item}
                    isUpdating={isUpdating}
                    onDelete={deleteFolder}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.emptyWrapper}>
              <p className={styles.emptyInfo}>{t('empty')}</p>
            </div>
          )}
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
